import {
  BlotterTable,
  BlotterTableFilters,
  Dialog,
  LoaderTalosIcon,
  Text,
  VStack,
  useBlotterObservables,
  useBlotterTable,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import { useMemo } from 'react';
import type { PreTradeResponseData } from '../../redux/preTradeApiTypes';
import { useHistoryColumns } from './useHistoryColumns';
import { useHistoryFilter, type HistoryFilter } from './useHistoryFilter';
import { useHistoryNavigation } from './useHistoryNavigation';

interface HistoryTabProps {
  sessionId: string;
  switchToScenarios: () => void;
  historyFilter?: HistoryFilter;
  onHistoryFilterChange?: (filter: HistoryFilter) => void;
}

export function HistoryTab({ sessionId, switchToScenarios, historyFilter, onHistoryFilterChange }: HistoryTabProps) {
  const { filteredScenarios, isHistoryDataLoading, blotterTableFilterProps, filterBuilderAccordion } = useHistoryFilter(
    { initialFilter: historyFilter, onFilterChange: onHistoryFilterChange }
  );
  const { handleOpenSession, pendingSessionId, setPendingSessionId, handleConfirmOpen } = useHistoryNavigation();
  const { columns, autoGroupColumnDef, aggFuncs } = useHistoryColumns({
    onOpenSession: handleOpenSession,
    openSessionId: sessionId,
    switchToScenarios,
  });

  const { dataObservable, errorObservable } = useBlotterObservables({ data: filteredScenarios });

  const gridOptions = useMemo<GridOptions<PreTradeResponseData>>(
    () => ({
      groupDisplayType: 'singleColumn',
      groupDefaultExpanded: -1,
      autoGroupColumnDef,
      aggFuncs,
    }),
    [aggFuncs, autoGroupColumnDef]
  );

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable,
    rowID: 'RequestID' satisfies keyof PreTradeResponseData,
    columns,
    gridOptions,
  });

  return (
    <VStack h="100%" alignItems="stretch">
      <BlotterTableFilters
        {...blotterTable.blotterTableFiltersProps}
        {...blotterTableFilterProps}
        {...filterBuilderAccordion}
      />
      {/* History data comes from a REST query (RTK Query), not a WebSocket observable like most blotters.
          We block rendering until the initial fetch completes to avoid flashing an empty grid. */}
      {isHistoryDataLoading ? (
        <VStack h="100%">
          <LoaderTalosIcon />
        </VStack>
      ) : (
        <BlotterTable {...blotterTable} />
      )}

      <Dialog
        isOpen={pendingSessionId !== null}
        close={() => setPendingSessionId(null)}
        title="Open historical session"
        confirmLabel="Open"
        onConfirm={handleConfirmOpen}
        dataTestId="history-session-confirm-dialog"
      >
        <Text>You have an active session. Opening this historical session will replace it. Continue?</Text>
      </Dialog>
    </VStack>
  );
}
